import * as XLSX from 'xlsx';

// Helper function to convert Excel column letter to index
export const colToIndex = (col) => {
  let result = 0;
  for (let i = 0; i < col.length; i++) {
    result = result * 26 + (col.charCodeAt(i) - 64);
  }
  return result - 1; // 0-based index
};

// Helper function to extract cell address parts
export const getCellIndices = (cellAddress) => {
  const match = cellAddress.match(/([A-Z]+)([0-9]+)/);
  const col = colToIndex(match[1]);
  const row = parseInt(match[2]) - 1; // 0-based index
  return { col, row };
};

// Helper function to check if cell coordinates are within a range
export const isCellInRange = (row, col, rangeStart, rangeEnd) => {
  const startIndices = getCellIndices(rangeStart);
  const endIndices = getCellIndices(rangeEnd);
  
  return (
    row >= startIndices.row && 
    row <= endIndices.row && 
    col >= startIndices.col && 
    col <= endIndices.col
  );
};

// Function to determine which court a game is played on based on cell position
export const determineCourtForGame = (row, col, courtRanges) => {
  for (const [courtName, ranges] of Object.entries(courtRanges)) {
    for (const range of ranges) {
      if (isCellInRange(row, col, range.startCell, range.endCell)) {
        return courtName;
      }
    }
  }
  return "Court not specified";
};

// Helper function to render a group table
export const renderGroupTable = (groupName, matches, groupTeams, division, filter) => (
  <div 
    className="group" 
    key={`${division}-${groupName}`} 
    style={{ display: filter === 'all' || filter === groupName ? 'block' : 'none' }}
    data-group={groupName}
  >
    <h3>{groupName}</h3>
    {groupTeams[division]?.[groupName]?.length > 0 && (
      <div className="team-list"><strong>Teams:</strong> {groupTeams[division]?.[groupName]?.join(', ')}</div>
    )}
    
    {/* Regular table view (displayed on desktop) */}
    <div className="table-view">
      <table>
        <thead>
          <tr>
            <th>Time</th>
            <th>Team A</th>
            <th>Score</th>
            <th>Score</th>
            <th>Team B</th>
            <th>Court</th>
            <th>Referee</th>
          </tr>
        </thead>
        <tbody>
          {matches.map((match, idx) => {
            const scoreA = match.scoreA !== null && match.scoreA !== undefined ? Number(match.scoreA) : null;
            const scoreB = match.scoreB !== null && match.scoreB !== undefined ? Number(match.scoreB) : null;
            const teamAWinner = scoreA !== null && scoreB !== null && scoreA > scoreB;
            const teamBWinner = scoreA !== null && scoreB !== null && scoreB > scoreA;
            
            return (
              <tr key={idx}>
                <td>{match.time}</td>
                <td className={teamAWinner ? "winner" : ""}>{match.teamA}</td>
                <td>{match.scoreA || '-'}</td>
                <td>{match.scoreB || '-'}</td>
                <td className={teamBWinner ? "winner" : ""}>{match.teamB}</td>
                <td>{match.court}</td>
                <td>{match.referee}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    
    {/* Bracket-style card view (displayed on mobile) */}
    <div className="card-view">
      {matches.map((match, idx) => {
        const scoreA = match.scoreA !== null && match.scoreA !== undefined ? Number(match.scoreA) : null;
        const scoreB = match.scoreB !== null && match.scoreB !== undefined ? Number(match.scoreB) : null;
        const teamAWinner = scoreA !== null && scoreB !== null && scoreA > scoreB;
        const teamBWinner = scoreA !== null && scoreB !== null && scoreB > scoreA;
        
        // Generate game number from index if not provided in match data
        const gameNumber = match.gameNumber || `Game ${idx + 1}`;
        
        return (
          <div key={idx} className="match-card">
            <div className="match-header">
              <div className="match-game">{gameNumber}</div>
              <div className="match-time">{match.time}</div>
            </div>
            <div className="match-teams">
              <div className={`team-row ${teamAWinner ? "winner" : ""}`}>
                <span className="team-name">{match.teamA}</span>
                <span className="team-score">{match.scoreA || '-'}</span>
              </div>
              <div className={`team-row ${teamBWinner ? "winner" : ""}`}>
                <span className="team-name">{match.teamB}</span>
                <span className="team-score">{match.scoreB || '-'}</span>
              </div>
            </div>
            <div className="match-details">
              <div className="match-court">Court: {match.court || 'TBD'}</div>
              <div className="match-referee">Referee: {match.referee || 'TBD'}</div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// Helper function to render a bracket
export const renderBracketRounds = (rounds) => (
  <div className="bracket-container">
    {rounds.map((round, roundIndex) => (
      <div className="round" key={roundIndex}>
        <h3>{round.round}</h3>
        {round.matches.map((match, matchIndex) => (
          <div className="match" key={matchIndex}>
            <strong>{match.teamA}</strong> {match.scoreA || '-'} vs {match.scoreB || '-'} <strong>{match.teamB}</strong>
            <div className="court">{match.court}</div>
            {match.time && <div className="time">{match.time}</div>}
          </div>
        ))}
      </div>
    ))}
  </div>
);

// Function to fetch and process Excel file
export const fetchExcel = async (sheetUrl) => {
  try {
    const response = await fetch(sheetUrl);
    const arrayBuffer = await response.arrayBuffer();
    
    // Parse the Excel file
    const workbook = XLSX.read(arrayBuffer, {
      type: 'array',
      cellStyles: true,
      cellDates: true,
      cellFormulas: true
    });
    
    return workbook;
  } catch (err) {
    console.error("Error fetching Excel:", err);
    throw new Error("Failed to load tournament schedule. Please try again later.");
  }
};

// Process each group with single or multiple cell ranges
// Process each group with single or multiple cell ranges
export const processGroupData = (groupConfigs, jsonData, courtRanges, getCellIndices, determineCourtForGame) => {
  const grouped = {};
  const teamsByGroup = {};
  
  Object.entries(groupConfigs).forEach(([groupName, config]) => {
    grouped[groupName] = [];
    teamsByGroup[groupName] = new Set();
    
    // Handle a single cell range
    const processCellRange = (teamARange, teamBRange, refereeRange) => {
      // Get start and end indices for team A
      const teamAStart = getCellIndices(teamARange.startCell);
      const teamAEnd = getCellIndices(teamARange.endCell);
      
      // Get start and end indices for team B
      const teamBStart = getCellIndices(teamBRange.startCell);
      const teamBEnd = getCellIndices(teamBRange.endCell);
      
      // Get start and end indices for referee
      const refereeStart = getCellIndices(refereeRange.startCell);
      const refereeEnd = getCellIndices(refereeRange.endCell);
      
      // Extract matches
      for (let i = 0; i <= teamAEnd.row - teamAStart.row; i++) {
        const rowIndexA = teamAStart.row + i;
        const rowIndexB = teamBStart.row + i;
        const rowIndexRef = refereeStart.row + i;
        
        const teamA = jsonData[rowIndexA]?.[teamAStart.col];
        const teamB = jsonData[rowIndexB]?.[teamBStart.col];
        const referee = jsonData[rowIndexRef]?.[refereeStart.col];
        
        // Try to get time from column A (index 0) for the current row
        let time = jsonData[rowIndexA]?.[0];
        
        // If time from column A is empty or invalid, check column V (index 21) for the same row
        const vColumnIndex = 21; // Column V is the 22nd column (0-indexed as 21)
        if (!time || time === 'Laikas' || (typeof time === 'string' && !time.includes(':'))) {
          time = jsonData[rowIndexA]?.[vColumnIndex];
        }
        
        // If we still don't have a valid time, use the config time
        if (!time || time === 'Laikas' || (typeof time === 'string' && !time.includes(':'))) {
          time = config.time;
        }
        
        // Format time if it's a Date object
        if (time instanceof Date) {
          // Format as HH:MM
          time = time.getHours().toString().padStart(2, '0') + ':' + 
                 time.getMinutes().toString().padStart(2, '0');
        }
        
        // Try to get scores - these would typically be in columns adjacent to team names
        // Adjust the column indices as needed for your Excel structure
        const scoreA = jsonData[rowIndexA]?.[teamAStart.col + 1] || null;
        const scoreB = jsonData[rowIndexB]?.[teamBStart.col - 1] || null;
        
        if (teamA && teamB) {
          teamsByGroup[groupName].add(teamA);
          teamsByGroup[groupName].add(teamB);
          
          // Determine the court based on cell position
          const court = determineCourtForGame(rowIndexA, teamAStart.col, courtRanges);
          
          grouped[groupName].push({
            time: time,
            teamA: teamA,
            teamB: teamB,
            scoreA: scoreA,
            scoreB: scoreB,
            referee: referee,
            court: court
          });
        }
      }
    };
    
    // Check if we have multiple cell ranges or just one
    if (Array.isArray(config.teamA)) {
      // Process multiple cell ranges
      for (let i = 0; i < config.teamA.length; i++) {
        processCellRange(config.teamA[i], config.teamB[i], config.referee[i]);
      }
    } else {
      // Process single cell range
      processCellRange(config.teamA, config.teamB, config.referee);
    }
  });
  
  // Convert Sets to Arrays for the teams
  const teamObj = {};
  Object.entries(teamsByGroup).forEach(([g, set]) => {
    teamObj[g] = Array.from(set);
  });
  
  return { grouped, teamObj };
};